import type { NotificationDetailAction } from "./notificationDetailActions"
import { resolveNotificationInternalPath } from "./notificationDetailNavigation"
import { normalizeReportTypeKey } from "./reportTypeRules"

function pickStr(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === "string" && v.trim()) return v.trim()
  }
  return undefined
}

function resolveLaneKey(reportType?: string | null): string {
  const key = normalizeReportTypeKey(reportType ?? "trade-analysis")
  if (key === "market-context-performance") return "market-context-performance"
  if (key === "trade-management") return "trade-management"
  return "trade-analysis"
}

/** Deep link to the finished report detail page. */
export function resolveReportReadyPath(details: unknown): string {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return "/reports"
  }
  const obj = details as Record<string, unknown>

  const fromUrl = pickStr(obj, [
    "action_url",
    "actionUrl",
    "report_url",
    "reportUrl",
  ])
  if (fromUrl) {
    const internal = resolveNotificationInternalPath(fromUrl)
    if (internal && (internal.startsWith("/reports/") || internal === "/reports")) {
      return internal
    }
  }

  const reportId = pickStr(obj, ["report_id", "reportId", "id"])
  if (reportId) {
    return `/reports/${encodeURIComponent(reportId)}`
  }

  return "/reports"
}

/** Retry from the generate wizard, prefilled with the failed report type when known. */
export function resolveReportFailedRetryPath(details: unknown): string {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return "/reports/new"
  }
  const obj = details as Record<string, unknown>
  const typeRaw = pickStr(obj, ["report_type", "reportType"])
  if (typeRaw) {
    const lane = resolveLaneKey(typeRaw)
    return `/reports/new?report_type=${encodeURIComponent(lane)}`
  }
  return "/reports/new"
}

export function buildReportReadyDetailActions(
  details: unknown
): NotificationDetailAction[] {
  return [
    {
      id: "view-report",
      rowLabel: "Report",
      buttonLabel: "View report",
      path: resolveReportReadyPath(details),
    },
  ]
}

export function buildReportFailedDetailActions(
  details: unknown
): NotificationDetailAction[] {
  return [
    {
      id: "try-again-report",
      rowLabel: "Report",
      buttonLabel: "Try again",
      path: resolveReportFailedRetryPath(details),
    },
  ]
}
